import { Activity, AlertTriangle, Gauge, Magnet, Wind } from 'lucide-react';
import {
  createAlertSeverityRing,
  createElectronFluxRing,
  createGeomagnticActivityRing,
  createImfBzRing,
  createSolarWindRing,
} from '@/lib/spaceWeatherCycle';

export type SpaceWeatherReport = {
  product_id?: string;
  issue_datetime?: string;
  pubDate?: string;
  message?: string;
  forecast?: unknown;
};

export type RingConfig = {
  id: string;
  label: string;
  description: string;
  unit?: string;
  waveform: string;
  icon: JSX.Element;
  parseValue: (report: SpaceWeatherReport) => { raw?: string; numeric?: number } | null;
  stageLabelForValue: (value: number | string | undefined) => string | undefined;
};

const severityOrder = ['none', 'watch', 'warning', 'alert'] as const;

const getMessageText = (report: SpaceWeatherReport) => {
  const message = report.message ?? '';
  const forecast = report.forecast ? String(report.forecast) : '';
  return `${message} ${forecast}`.trim();
};

const parseNumber = (text: string, regex: RegExp) => {
  const match = text.match(regex);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
};

const parseSeverity = (productId?: string) => {
  const value = productId?.toLowerCase() ?? '';
  if (value.includes('alert')) return 'alert';
  if (value.includes('warning')) return 'warning';
  if (value.includes('watch')) return 'watch';
  return 'none';
};

export const spaceWeatherWaveformRingConfigs: RingConfig[] = [
  {
    id: 'geomagnetic-activity',
    label: 'Geomagnetic Activity',
    description: 'K-index (0-9) mapped to G-scale levels.',
    unit: 'Kp',
    waveform: 'sine',
    icon: <Magnet className="h-4 w-4" />,
    parseValue: (report) => {
      const message = getMessageText(report);
      const kIndex = parseNumber(message, /k[\s-]*index[\s-]*([0-9]{1,2})/i);
      if (kIndex === null) return null;
      return { raw: kIndex.toString(), numeric: kIndex };
    },
    stageLabelForValue: (value) => {
      if (typeof value !== 'number') return undefined;
      const ring = createGeomagnticActivityRing(value);
      return ring.labels?.[ring.currentStage ?? 0];
    },
  },
  {
    id: 'solar-wind-pressure',
    label: 'Solar Wind Speed',
    description: 'Solar wind speed (km/s) used for pressure cycle.',
    unit: 'km/s',
    waveform: 'triangle',
    icon: <Wind className="h-4 w-4" />,
    parseValue: (report) => {
      const message = getMessageText(report);
      const speed = parseNumber(message, /([0-9]+(?:\.[0-9]+)?)\s*km\/s/i);
      if (speed === null) return null;
      return { raw: speed.toString(), numeric: speed };
    },
    stageLabelForValue: (value) => {
      if (typeof value !== 'number') return undefined;
      const ring = createSolarWindRing(value);
      return ring.labels?.[ring.currentStage ?? 0];
    },
  },
  {
    id: 'electron-flux',
    label: 'Electron Flux',
    description: 'High-energy electron flux levels.',
    unit: 'flux',
    waveform: 'default',
    icon: <Activity className="h-4 w-4" />,
    parseValue: (report) => {
      const message = getMessageText(report);
      const flux = parseNumber(message, /([0-9]+(?:\.[0-9]+)?)\s*(?:electrons|electron|flux)/i);
      if (flux === null) return null;
      return { raw: flux.toString(), numeric: flux };
    },
    stageLabelForValue: (value) => {
      if (typeof value !== 'number') return undefined;
      const ring = createElectronFluxRing(value);
      return ring.labels?.[ring.currentStage ?? 0];
    },
  },
  {
    id: 'imf-bz',
    label: 'IMF Bz Polarity',
    description: 'Interplanetary magnetic field Bz component.',
    unit: 'nT',
    waveform: 'sine',
    icon: <Gauge className="h-4 w-4" />,
    parseValue: (report) => {
      const message = getMessageText(report);
      const bz = parseNumber(message, /bz\s*=?\s*(-?[0-9]+(?:\.[0-9]+)?)/i);
      if (bz === null) return null;
      return { raw: bz.toString(), numeric: bz };
    },
    stageLabelForValue: (value) => {
      if (typeof value !== 'number') return undefined;
      const ring = createImfBzRing(value);
      return ring.labels?.[ring.currentStage ?? 0];
    },
  },
  {
    id: 'alert-severity',
    label: 'Alert Severity',
    description: 'Watch/Warning/Alert state over time.',
    waveform: 'square',
    icon: <AlertTriangle className="h-4 w-4" />,
    parseValue: (report) => {
      const severity = parseSeverity(report.product_id);
      return { raw: severity, numeric: severityOrder.indexOf(severity) };
    },
    stageLabelForValue: (value) => {
      const severity = typeof value === 'string'
        ? value
        : severityOrder[value ?? 0];
      const ring = createAlertSeverityRing(severity as 'none' | 'watch' | 'warning' | 'alert');
      return ring.labels?.[ring.currentStage ?? 0];
    },
  },
];
